import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/20/solid";
import {
  JobStatus,
  PipelineStageType,
  useCreateLitePaymentMutation,
  useCurrentSessionQuery,
  useJobsApplicationsQuery,
  useJobsQuery,
  JobsOrderBy,
} from "../../graphql/generated/graphql";
import CardModal from "../../components/Modal/CardModal";
import GiftPopup from "../../components/popup/Popup";
import Logo from "../../image/logo.png";
import CreateJobForm from "../../components/CreateJobForm/CreateJobForm";
import JobItem from "../../components/JobItem/JobItem";
import Modal from "../../components/Modal/Modal";

export default function PublishedJobs() {
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data: sessionData, loading: sessionLoading } =
    useCurrentSessionQuery();
  const {
    data: jobsData,
    loading: jobsLoading,
    error: jobsError,
    refetch: refetchJobs,
  } = useJobsQuery({
    variables: {
      orderBy: [JobsOrderBy.CreatedAtDesc],
    },
  });
  const { data: applicationsData } = useJobsApplicationsQuery();

  const [createLitePaymentMutation] = useCreateLitePaymentMutation();

  const currentCompany =
    sessionData?.currentCompany?.translations?.nodes?.[0]?.name || "شرکت شما";
  const companyLogo = sessionData?.currentCompany?.logoUrl || Logo;

  const allJobs = jobsData?.jobs?.nodes || [];

  const hireStageCandidates =
    applicationsData?.jobsApplications?.nodes
      ?.filter(
        (application) =>
          application?.candidate?.jobsApplications.nodes[0].pipelineStage
            ?.type === PipelineStageType.Hire &&
          application?.candidate.jobsApplications.nodes[0].job?.id &&
          application?.candidate?.translations?.nodes?.[0]?.name
      )
      .reduce((acc: { [key: string]: Set<string> }, application) => {
        const jobId = application?.candidate?.jobsApplications.nodes[0].job?.id;
        const candidateName =
          application.candidate?.translations?.nodes?.[0]?.name;

        if (jobId && candidateName) {
          if (!acc[jobId]) acc[jobId] = new Set();
          acc[jobId].add(candidateName);
        }
        return acc;
      }, {}) || {};

  const hireStageCandidatesArray = Object.keys(hireStageCandidates).reduce(
    (acc: { [key: string]: string[] }, jobId) => {
      acc[jobId] = Array.from(hireStageCandidates[jobId]);
      return acc;
    },
    {}
  );

  const publishedJobs = allJobs.filter(
    (job) => job.status === JobStatus.Published
  );

  const sortedJobs = [
    ...publishedJobs.filter(
      (job) => hireStageCandidatesArray[job.id]?.length > 0
    ),
    ...publishedJobs.filter((job) => !hireStageCandidatesArray[job.id]?.length),
  ];

  useEffect(() => {
    if (!sessionLoading && sessionData) {
      const companyId = sessionData.currentCompany?.id;
      const companyLogoUrl = sessionData.currentCompany?.logoUrl;

      if (!companyLogoUrl && companyId) {
        navigate(`/update-company/${companyId}`);
      }

      const hasActivePlan =
        sessionData.currentCompany?.activeInvoice?.nodeId &&
        sessionData.currentCompany?.activeInvoice?.subscription?.nodeId;

      const giftActivated = localStorage.getItem("giftActivated");

      if (!hasActivePlan && !giftActivated) {
        setShowGiftModal(true);
      }
    }
  }, [sessionLoading, sessionData, navigate]);

  const handleGiftConfirmation = async () => {
    setLoading(true);
    try {
      const response = await createLitePaymentMutation();
      if (response.data?.createLitePayment?.success) {
        toast.success("هدیه با موفقیت فعال شد!");
        localStorage.setItem("giftActivated", "true");
      } else {
        toast.error("ارسال هدیه ناموفق بود. لطفاً دوباره امتحان کنید.");
      }
    } catch {
      toast.error("خطایی رخ داد. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
      setShowGiftModal(false);
    }
  };

  const handleJobCreated = () => {
    setModalOpen(false);
    refetchJobs();
  };

  const handleLogout = () => {
    setLogoutModalOpen(false);
    navigate("/logout");
  };

  if (jobsLoading || sessionLoading || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  return (
    <main>
      {showGiftModal && (
        <GiftPopup
          isModalOpen={showGiftModal}
          setIsModalOpen={setShowGiftModal}
          giftStatus={sessionData?.currentCompany?.activeInvoice?.deletedAt}
          handleGiftConfirmation={handleGiftConfirmation}
          loading={loading}
          isRequired={true}
        />
      )}
      <div className="w-full flex justify-between gap-4 items-center">
        <Link
          target="__blank"
          to={"https://jobs.karjoo.plus/qazvin"}
          className="h-[38px] flex gap-1 px-1.5 mt-2 mx-2 border-2 border-primary rounded-lg justify-center items-center"
        >
          <ArrowTopRightOnSquareIcon className="h-6 w-6 text-primary" />
          <p className="text-sm text-primary">مشاهده آگهی ها</p>
        </Link>
        <button
          onClick={() => setLogoutModalOpen(true)}
          className="w-10 border-2 border-gray2 rounded-full h-10 bg-opacity-80 bg-red-700 font-semibold text-gray2 flex items-center justify-center mt-2 ml-2"
        >
          <ArrowLeftStartOnRectangleIcon className="h-6 w-6 text-gray-200" />
        </button>
      </div>
      <CardModal
        open={isLogoutModalOpen}
        setOpen={setLogoutModalOpen}
        request={undefined}
      >
        <h2 className="text-lg font-bold mb-8 text-center text-pretty">
          برای خروج از حساب کاربری اطمینان دارید؟
        </h2>
        <div className="flex justify-around">
          <button
            onClick={handleLogout}
            className="px-8 py-2 bg-red-500 text-white rounded-md font-medium"
          >
            بله
          </button>
          <button
            onClick={() => setLogoutModalOpen(false)}
            className="px-8 py-2 bg-gray-200 text-gray-800 rounded-md font-medium"
          >
            خیر
          </button>
        </div>
      </CardModal>
      <div className="relative z-0 px-2 m-auto lg:max-w-[30%] max-w-[90%] my-10">
        {sessionLoading ? (
          <div className="animate-pulse flex justify-center items-center h-40 w-auto m-auto bg-gray-200 rounded-lg">
            <div className="h-32 w-32 bg-gray-300 rounded-full"></div>
          </div>
        ) : (
          <img src={companyLogo} alt="Logo" className="h-40 w-auto m-auto" />
        )}
        <h1 className="text-center font-bold text-xl mt-2 mb-6">
          {currentCompany}
        </h1>
        {jobsError ? (
          <div className="text-center text-red-500">خطا در بارگذاری مشاغل.</div>
        ) : sortedJobs.length > 0 ? (
          sortedJobs.map((job) => (
            <JobItem
              key={job.id}
              job={job}
              hireStageCandidates={hireStageCandidatesArray[job.id] || []}
            />
          ))
        ) : (
          <div className="text-center text-gray-500 my-2">شغلی وجود ندارد.</div>
        )}
        <button
          onClick={() => setModalOpen(true)}
          className="w-full border-2 border-gray2 rounded-lg p-2 h-11 bg-opacity-80 font-semibold text-gray2 flex items-center justify-center"
        >
          افزودن شغل جدید +
        </button>
        <Modal open={isModalOpen} setOpen={setModalOpen} request={undefined}>
          <CreateJobForm onSuccess={handleJobCreated} setOpen={setModalOpen} />
        </Modal>
      </div>
    </main>
  );
}
